<template>
    <div>
        <Breadcrumbs :type="type" :header="title" :url="url" :head="head"></Breadcrumbs>
        <trashed-message v-if="userManual.deleted_at" class="mb-6" @restore="restore"> This User Manual has been
            deleted.
        </trashed-message>
        <div class="max-w-5xl bg-white rounded-md shadow overflow-hidden">
            <form @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <text-input v-model="form.title" :error="form.errors.title" class="pb-8 pr-6 w-full lg:w-1/2"
                        @keyup="slugBuild" label="Title" required="true" placeholder="i.e How to Create an Account" />
                    <text-input v-model="form.slug" :error="form.errors.slug" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Slug" required="true" placeholder="i.e how-to-create-an-account" />

                    <select-input v-model="form.cat_id" :error="form.errors.cat_id" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Category" required="true">
                        <option :value=null>Select Category</option>
                        <option v-for="userManualC in userManualCat" :key="userManualC.id" :value='userManualC.id'>
                            {{ userManualC.title }}
                        </option>
                    </select-input>

                    <text-input v-model="form.order" :error="form.errors.order" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Order" type="number" placeholder="0" />

                    <textarea-input v-model="form.description" :error="form.errors.description"
                        class="pb-8 pr-6 w-full lg:w-1/1" label="Short Description"
                        placeholder="Brief description of this manual..." />

                    <div class="pb-8 pr-6 w-full lg:w-1/1">
                        <label class="form-label">Content:</label>
                        <QuillEditor v-model:content="form.content" theme="snow" contentType="html" :toolbar="[
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ 'header': 1 }, { 'header': 2 }],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'script': 'sub' }, { 'script': 'super' }],
                            [{ 'indent': '-1' }, { 'indent': '+1' }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'align': [] }],
                            ['link', 'image'],
                            ['clean']
                        ]" style="min-height: 300px;" />
                        <div v-if="form.errors.content" class="form-error">{{ form.errors.content }}</div>
                    </div>

                    <div class="pb-8 pr-6 w-full lg:w-1/1" v-if="userManual.attachment">
                        <label class="form-label">Current Attachment:</label>
                        <p class="text-sm text-gray-600">{{ userManual.attachment }}</p>
                    </div>

                    <file-input v-model="form.attachment" :error="form.errors.attachment"
                        class="pb-8 pr-6 w-full lg:w-1/1" type="file" accept=".pdf,.doc,.docx"
                        label="Attachment (PDF/DOC)" />

                    <status-toogle-button v-model="form.status" :error="form.errors.status" label="Status"
                        class="pb-8 pr-6 w-full lg:w-1/1"></status-toogle-button>
                </div>
                <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <button v-if="!userManual.deleted_at && permission.edit > 0" class="text-red-600 hover:underline"
                        tabindex="-1" type="button" @click="destroy">
                        <delete-icon></delete-icon>
                    </button>
                    <loading-button v-if="permission.edit > 0" :loading="form.processing" class="btn-indigo ml-auto"
                        type="submit">
                        Update User Manual
                    </loading-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import Breadcrumbs from '@/Shared/Breadcrumbs'
    import Button from '@/Shared/Button'
    import DeleteIcon from '@/Shared/DeleteIcon'
    import FileInput from '@/Shared/FileInput'
    import Icon from '@/Shared/Icon'
    import Layout from '@/Shared/Layout'
    import LoadingButton from '@/Shared/LoadingButton'
    import SelectInput from '@/Shared/SelectInput'
    import StatusToogleButton from '@/Shared/StatusToogleButton'
    import TextareaInput from '@/Shared/TextareaInput'
    import TextInput from '@/Shared/TextInput'
    import TrashedMessage from '@/Shared/TrashedMessage'
    import { Head, Link, useForm } from '@inertiajs/inertia-vue3'
    import { ref, toRefs, inject } from 'vue'
    import { QuillEditor } from '@vueup/vue-quill'
    import '@vueup/vue-quill/dist/vue-quill.snow.css'

    export default {
        components: {
            Head,
            Icon,
            Link,
            LoadingButton,
            SelectInput,
            TextInput,
            TrashedMessage,
            StatusToogleButton,
            Breadcrumbs,
            DeleteIcon,
            Button,
            TextareaInput,
            FileInput,
            QuillEditor
        },
        layout: Layout,
        props: {
            userManual: Object,
            permission: Object,
            userManualCat: Array
        },
        remember: 'form',
        setup(props) {
            const { userManual, permission, userManualCat } = toRefs(props)
            const form = useForm({
                title: userManual.value.title,
                slug: userManual.value.slug,
                description: userManual.value.description,
                content: userManual.value.content,
                cat_id: userManual.value.cat_id,
                order: userManual.value.order,
                status: userManual.value.status,
                attachment: null
            })
            const type = ref('edit')
            const title = ref('User Manual')
            const url = ref('/backend/user-manuals')
            const head = ref(userManual.value.title)
            const swal = inject('$swal')
            const update = () => {
                swal({
                    title: 'Are you sure?',
                    text: "You want to proceed with this action",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        form.post(`/backend/user-manuals/${userManual.value.id}`, { forceFormData: true })
                        swal.fire(
                            'Updated!',
                            'Your infomation has been Updated.',
                            'success'
                        )
                    }
                });
            }
            const destroy = () => {
                swal({
                    title: 'Are you sure?',
                    text: "You won't be able to revert this!",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        form.delete(`/backend/user-manuals/${userManual.value.id}`)
                        swal.fire(
                            'Deleted!',
                            'Your file has been deleted.',
                            'success'
                        )
                    }
                });
            }
            const restore = () => {
                swal({
                    title: 'Are you sure?',
                    text: "You want to proceed with this action",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                }).then((result) => {
                    if (result.isConfirmed) {
                        form.put(`/backend/user-manuals/${userManual.value.id}/restore`)
                        swal.fire(
                            'Restored!',
                            'Your file has been Restored.',
                            'success'
                        )
                    }
                });
            }
            const slugBuild = () => {
                form.slug = form.title.replace(/\s/g, '-').toLowerCase()
            }
            return {
                form,
                update,
                destroy,
                restore,
                type,
                title,
                url,
                head,
                slugBuild,
                userManualCat
            }
        }
    }
</script>
